// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Location from "@sc/XDL/Icons/Location";
import { color, mergeWithCss, safeJsonParse, xScreen } from "@sc/XDL/Utilities";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface HeaderWarehouseAddressStyles {
    warehouseAddressWrapper?: InjectableCss;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    titleLabelTypography?: TypographyPresentationProps;
}

export const headerWarehouseAddress: HeaderWarehouseAddressStyles = {
    warehouseAddressWrapper: {
        css: css`
            color: ${color("common.ui1")};
            display: flex;
            align-items: center;
            column-gap: 4px;
        `,
    },
    titleIcon: {
        size: 14,
        css: css`
            opacity: 0.66;
            margin-right: 4px;
            color: ${color("common.ui1")};
            margin-top: -2px;
            &,
            svg {
                width: 11px;
            }
            ${xScreen("lte", "md")} {
                color: ${color("text.link")};
            }
        `,
    },
    titleLabelTypography: {
        size: 14,
        lineHeight: "22px",
        css: css`
            ${xScreen("lte", "md")} {
                color: ${color("text.main")};
            }
        `,
    },
    titleTypography: {
        size: 14,
        lineHeight: "22px",
        css: css`
            ${xScreen("lte", "md")} {
                color: ${color("text.link")};
            }
        `,
    },
};
interface OwnProps extends WidgetProps {
    fields: {};
    extendedStyles?: HeaderWarehouseAddressStyles;
}

const HeaderWarehouseAddress: FC<Props> = ({ id, session, extendedStyles }) => {
    const [styles] = React.useState(() => mergeWithCss(headerWarehouseAddress, extendedStyles));

    if (!session?.pickUpWarehouse || !session?.pickUpWarehouse?.description) {
        return null;
    }
    const defaultWarehouseInfo = session?.properties?.warehouseInfo
        ? safeJsonParse(session.properties.warehouseInfo).description
        : "";

    return (
        <>
            <StyledWrapper {...styles.warehouseAddressWrapper} data-test-selector="header_branch">
                <Icon {...styles.titleIcon} src={Location} />
                <Typography {...styles.titleLabelTypography}>
                    {session.fulfillmentMethod === FulfillmentMethod.Ship
                        ? translate("Your Branch")
                        : translate("Your Pick Up Location")}
                    :
                </Typography>
                <Typography {...styles.titleTypography} data-test-selector="header_shipToLabel">
                    {session.fulfillmentMethod === FulfillmentMethod.Ship
                        ? defaultWarehouseInfo
                        : session?.pickUpWarehouse?.description}
                </Typography>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(HeaderWarehouseAddress),
    definition: {
        displayName: "Header Warehouse Address",
        icon: "location-dot",
        group: "Common",
    },
};

export default widgetModule;
